import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import api from "./lib/api";
import Layout from "./components/Layout";
import NotReached from "./components/NotReached";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Campaigns from "./pages/Campaigns";
import Offers from "./pages/Offers";
import Networks from "./pages/Networks";
import TrafficSources from "./pages/TrafficSources";
import Clicks from "./pages/Clicks";
import ClickMap from "./pages/ClickMap";
import Customers from "./pages/Customers";
import Reconciliation from "./pages/Reconciliation";
import LinkGenerator from "./pages/LinkGenerator";
import Conversions from "./pages/Conversions";
import Reports from "./pages/Reports";
import PostbackTester from "./pages/PostbackTester";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
    if (!sessionId) {
      navigate("/login");
      return;
    }
    api
      .post("/auth/session", { session_id: sessionId })
      .then((res) => {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { state: { user: res.data }, replace: true });
        setUser(res.data);
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-slate-500 text-sm font-medium animate-pulse">Signing you in…</div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-400 text-sm animate-pulse">Loading…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const GATE_KEY = "osl_gate";
const GATE_VALUE = "nikhil-9696";

function AppRouter() {
  const location = useLocation();
  if (location.pathname === "/admin/nikhil/9696") {
    localStorage.setItem(GATE_KEY, GATE_VALUE);
    return <Navigate to="/login" replace />;
  }
  if (localStorage.getItem(GATE_KEY) !== GATE_VALUE) return <NotReached />;
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
      <Route path="/offers" element={<ProtectedRoute><Offers /></ProtectedRoute>} />
      <Route path="/networks" element={<ProtectedRoute><Networks /></ProtectedRoute>} />
      <Route path="/traffic-sources" element={<ProtectedRoute><TrafficSources /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/reconciliation" element={<ProtectedRoute><Reconciliation /></ProtectedRoute>} />
      <Route path="/link-generator" element={<ProtectedRoute><LinkGenerator /></ProtectedRoute>} />
      <Route path="/clicks" element={<ProtectedRoute><Clicks /></ProtectedRoute>} />
      <Route path="/click-map" element={<ProtectedRoute><ClickMap /></ProtectedRoute>} />
      <Route path="/conversions" element={<ProtectedRoute><Conversions /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/postback-tester" element={<ProtectedRoute><PostbackTester /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
